import { useMemo } from "react";

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { getJourneyStatus, hasCancelledLeg } from "@repo/ui/helpers/legStatus";
import type { CheckinEligibilityData } from "@repo/ui/types/checkinEligibility";
import type { BookingSummary, Journey, Passenger } from "@repo/ui/types/manageBooking";

import { useAppSelector } from "../hooks";

export interface ManageBookingState {
  raw: BookingSummary | null;
  selectedJourney: Journey["journeyKey"] | null;
  selectedPassengers: Passenger["passengerKey"][];
  selectedJourneyEligibility: CheckinEligibilityData | null;
  refundEligibility: boolean | null;
}

const initialState: ManageBookingState = {
  raw: null,
  selectedJourney: null,
  selectedPassengers: [],
  selectedJourneyEligibility: null,
  refundEligibility: null,
};

const manageBookingSlice = createSlice({
  name: "manageBooking",
  initialState,
  reducers: {
    setManageBooking: (state, action: PayloadAction<BookingSummary | null>) => {
      const booking = action.payload;

      // Save raw booking data
      state.raw = booking;
    },
    setSelectedJourney: (state, action: PayloadAction<Journey["journeyKey"] | null>) => {
      state.selectedJourney = action.payload;
    },
    setSelectedPassengers: (state, action: PayloadAction<Passenger["passengerKey"][]>) => {
      state.selectedPassengers = action.payload;
    },
    setSelectedJourneyEligibility: (
      state,
      action: PayloadAction<CheckinEligibilityData | null>,
    ) => {
      state.selectedJourneyEligibility = action.payload;
    },
    setRefundEligibility: (state, action: PayloadAction<boolean | null>) => {
      state.refundEligibility = action.payload;
    },
    resetManageBooking: () => initialState,
  },
});

export const {
  setManageBooking,
  setSelectedJourney,
  resetManageBooking,
  setSelectedPassengers,
  setSelectedJourneyEligibility,
  setRefundEligibility,
} = manageBookingSlice.actions;

export default manageBookingSlice.reducer;

// Selectors / Hooks
export const useManageBooking = () => useAppSelector((s) => s.manageBooking.raw);

export const useSelectedJourneyKey = () => useAppSelector((s) => s.manageBooking.selectedJourney);
export const useSelectedJourney = () =>
  useAppSelector((s) =>
    s.manageBooking.raw?.journeys?.find(
      (j: Journey) => j.journeyKey === s.manageBooking.selectedJourney,
    ),
  );

// Derived from the latest bookingSummary each time; nothing extra is stored in redux.
export const useHasCancelledLeg = () =>
  useAppSelector((s) => hasCancelledLeg(s.manageBooking.raw?.journeys));

export const useJourneyStatuses = () => {
  const journeys = useAppSelector((s) => s.manageBooking.raw?.journeys);
  return useMemo(
    () => new Map(journeys?.map((j) => [j.journeyKey, getJourneyStatus(j)])),
    [journeys],
  );
};

export const useNumericRecordLocator = () => {
  const NumRecordLocator = useAppSelector(
    (s) => s.booking?.bookingDetails?.locators?.numericRecordLocator,
  );
  return NumRecordLocator || "";
};
export const useJourneyPassengers = () =>
  useAppSelector((s) => s.manageBooking.raw?.passengers || []);

export const useRecordLocator = () => useAppSelector((s) => s.manageBooking.raw?.recordLocator);

export const usePassengersCount = () =>
  useAppSelector((s) => s.manageBooking.raw?.passengerCounts?.total ?? 0);

export const useStationCode = () =>
  useAppSelector((s) => s.manageBooking.raw?.journeys[0]?.segments[0]?.departure?.code);

export const useSelectedPassengerCheckin = () =>
  useAppSelector((s) => s.manageBooking.selectedPassengers);

export const useSelectedJourneyEligibility = () =>
  useAppSelector((s) => s.manageBooking.selectedJourneyEligibility);

export const useRefundEligibility = () => useAppSelector((s) => s.manageBooking.refundEligibility);
